/**
 * 真实入账判定：扫充值订单 CREDITED / 已入账，金额按 USDC/入账 USD ≥ 阈值。
 * 禁止用 wallet/balances、activityDusd、points。
 */
import { getMixPayOnrampOrders } from '@/api/mixpay';
import { getTopPayOnrampOrders } from '@/api/toppay';
import { getOnrampOrders } from '@/api/recharge';
import { coboDepositOrders } from '@/api/trade';
import {
  extractCoboDisplayAmount,
  isCoboOrderCompletedOrder,
  normalizeCoboOrdersPage,
} from '@/utils/cobo-deposit-order';
import {
  isMixPayLedgerCredited,
  MIXPAY_ORDER_STATUS,
  normalizeMixPayOrderStatus,
  normalizeMixPayOrdersPage,
} from '@/utils/mixpay-order-status';
import {
  normalizeOnrampOrderStatus,
  normalizeOnrampOrdersPage,
  ONRAMP_ORDER_STATUS,
} from '@/utils/onramp-order-status';
import { normalizeTopPayOrderStatus, normalizeTopPayOrdersPage } from '@/utils/toppay-order-status';

/** 真实入账金额阈值（USD / USDC） */
export const REAL_DEPOSIT_CREDIT_MIN_USD = 0.1;

const SCAN_PAGE_SIZE = 50;
const CACHE_TTL_MS = 30_000;

/** @type {{ at: number, hasRealDeposit: boolean|null, creditedCount: number, maxCreditedUsd: number } | null} */
let cache = null;

/** @returns {number} */
export function readDepositUsdAmount(raw) {
  if (raw == null || raw === '') return NaN;
  if (typeof raw === 'object' && raw != null && 's' in raw) {
    return Number(raw.s);
  }
  return Number(raw);
}

function meetsCreditThreshold(usd) {
  return Number.isFinite(usd) && usd >= REAL_DEPOSIT_CREDIT_MIN_USD;
}

/**
 * MixPay：SUCCESS + ledgerCredited，金额用 quoteAmount（USD）。
 * @param {object} order
 * @returns {number|null} 入账 USD；未入账返回 null
 */
export function mixPayCreditedUsd(order) {
  if (!order) return null;
  if (normalizeMixPayOrderStatus(order.status) !== MIXPAY_ORDER_STATUS.SUCCESS) return null;
  if (!isMixPayLedgerCredited(order)) return null;
  const usd = readDepositUsdAmount(order.quoteAmount);
  return meetsCreditThreshold(usd) ? usd : null;
}

/**
 * TopPay：SUCCESS + ledgerCredited，金额用 creditUsd。
 * @param {object} order
 * @returns {number|null}
 */
export function topPayCreditedUsd(order) {
  if (!order) return null;
  if (normalizeTopPayOrderStatus(order.status) !== 'SUCCESS') return null;
  if (order.ledgerCredited !== true) return null;
  const usd = readDepositUsdAmount(order.creditUsd);
  return meetsCreditThreshold(usd) ? usd : null;
}

/**
 * Alchemy onramp：FINISHED 为真实入账；优先 credit/crypto USD，其次 fiat=USD 的 fiatAmount。
 * @param {object} order
 * @returns {number|null}
 */
export function alchemyCreditedUsd(order) {
  if (!order) return null;
  if (normalizeOnrampOrderStatus(order.status) !== ONRAMP_ORDER_STATUS.FINISHED) return null;
  const direct = readDepositUsdAmount(
    order.creditUsd ??
      order.cryptoAmount ??
      order.cryptoQuantity ??
      order.crypto_quantity ??
      order.crypto_amount ??
      order.usdcAmount ??
      order.receiveAmount
  );
  if (meetsCreditThreshold(direct)) return direct;
  const fiat = String(order.fiat ?? order.fiatCurrency ?? '')
    .trim()
    .toUpperCase();
  if (fiat === 'USD' || fiat === 'USDC') {
    const fiatAmt = readDepositUsdAmount(order.fiatAmount);
    if (meetsCreditThreshold(fiatAmt)) return fiatAmt;
  }
  // FINISHED 且无金额字段：保守不计（避免把体验金误判）；多数法币单有 fiatAmount
  return null;
}

/**
 * Cobo：status=5（含 CREDITED 别名），金额用入账展示额。
 * @param {object} order
 * @returns {number|null}
 */
export function coboCreditedUsd(order) {
  if (!order || !isCoboOrderCompletedOrder(order)) return null;
  const usd = Number(extractCoboDisplayAmount(order));
  return meetsCreditThreshold(usd) ? usd : null;
}

/**
 * @param {object[]} rows
 * @param {(row: object) => number|null} pickUsd
 * @returns {{ count: number, maxUsd: number }}
 */
function summarizeCreditedHits(rows, pickUsd) {
  let count = 0;
  let maxUsd = 0;
  const list = Array.isArray(rows) ? rows : [];
  for (const row of list) {
    const usd = pickUsd(row);
    if (usd == null) continue;
    count += 1;
    if (usd > maxUsd) maxUsd = usd;
  }
  return { count, maxUsd };
}

export function invalidateRealDepositCreditCache() {
  cache = null;
}

/**
 * 扫各通道充值订单：是否存在真实 CREDITED 入账（USD ≥ 0.1），并取历史最高入账额。
 * activityDusd / points / wallet balances 一律不参与。
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<{ hasRealDeposit: boolean, creditedCount: number, maxCreditedUsd: number, unknown: boolean }>}
 */
export async function resolveRealDepositCreditState(opts = {}) {
  const force = Boolean(opts.force);
  const now = Date.now();
  if (
    !force &&
    cache &&
    now - cache.at < CACHE_TTL_MS &&
    typeof cache.hasRealDeposit === 'boolean'
  ) {
    return {
      hasRealDeposit: cache.hasRealDeposit,
      creditedCount: cache.creditedCount,
      maxCreditedUsd: Number(cache.maxCreditedUsd) || 0,
      unknown: false,
    };
  }

  const page = { pageNum: 1, pageSize: SCAN_PAGE_SIZE };
  const settled = await Promise.allSettled([
    getMixPayOnrampOrders(page),
    getTopPayOnrampOrders(page),
    getOnrampOrders(page),
    coboDepositOrders(page),
  ]);

  const anyOk = settled.some((r) => r.status === 'fulfilled');
  if (!anyOk) {
    return { hasRealDeposit: false, creditedCount: 0, maxCreditedUsd: 0, unknown: true };
  }

  let creditedCount = 0;
  let maxCreditedUsd = 0;
  const [mix, top, alchemy, cobo] = settled;

  const merge = (summary) => {
    creditedCount += summary.count;
    if (summary.maxUsd > maxCreditedUsd) maxCreditedUsd = summary.maxUsd;
  };

  if (mix.status === 'fulfilled') {
    merge(summarizeCreditedHits(normalizeMixPayOrdersPage(mix.value).rows, mixPayCreditedUsd));
  }
  if (top.status === 'fulfilled') {
    merge(summarizeCreditedHits(normalizeTopPayOrdersPage(top.value).rows, topPayCreditedUsd));
  }
  if (alchemy.status === 'fulfilled') {
    merge(summarizeCreditedHits(normalizeOnrampOrdersPage(alchemy.value).rows, alchemyCreditedUsd));
  }
  if (cobo.status === 'fulfilled') {
    merge(summarizeCreditedHits(normalizeCoboOrdersPage(cobo.value).rows, coboCreditedUsd));
  }

  const hasRealDeposit = creditedCount > 0;
  cache = { at: now, hasRealDeposit, creditedCount, maxCreditedUsd };
  return { hasRealDeposit, creditedCount, maxCreditedUsd, unknown: false };
}

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<boolean|null>} true/false；接口全失败时 null（未知）
 */
export async function hasRealCreditedDeposit(opts = {}) {
  const state = await resolveRealDepositCreditState(opts);
  if (state.unknown) return null;
  return state.hasRealDeposit;
}

/**
 * 是否仍为「首次充值用户」：尚无任何 CREDITED 真实入账（USD ≥ 0.1）。
 * 不以 wallet/balances、activityDusd、points 为准。
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<boolean|null>} true=从未真实充值；false=已有真实入账；null=订单接口未知
 */
export async function isFirstDepositUser(opts = {}) {
  const hasReal = await hasRealCreditedDeposit(opts);
  if (hasReal == null) return null;
  return !hasReal;
}

/**
 * 当前这笔成功是否为「首次」真实入账（订单侧 CREDITED ≥ 0.1 计数 ≤ 1）。
 * 列表滞后 count=0 时，用本笔 amount ≥ 0.1 视为首次。
 * @param {{ amount?: unknown }} [value]
 * @returns {Promise<boolean>}
 */
export async function isFirstRealCreditedDepositSuccess(value = {}) {
  invalidateRealDepositCreditCache();
  const state = await resolveRealDepositCreditState({ force: true });
  if (state.unknown) {
    const amt = readDepositUsdAmount(value?.amount);
    return meetsCreditThreshold(amt);
  }
  if (state.creditedCount >= 2) return false;
  if (state.creditedCount === 1) return true;
  // 列表尚未看到本笔
  return meetsCreditThreshold(readDepositUsdAmount(value?.amount));
}
